// Batch-06: central contract strings for the event-driven notification system.
// Definition keys and module keys are case-sensitive backend contract strings —
// import these constants instead of repeating raw strings across components.
// Catalogue: FRONTEND_NOTIFICATION_CATALOG (Mudassir, Sep-17).

// moduleKey = the first segment of a definition key (before the first dot).
// Used with the sidebar AsideBar `notifications.modules` counts.
export const NotificationModules = {
  Actors: 'actors',
  Chat: 'chat',
  CollegeAdmissions: 'college-admissions',
  Commerce: 'commerce',
  EmployeeFinances: 'employee-finances',
  IdeaFactory: 'idea-factory',
  Identity: 'identity',
  Internships: 'internships',
  Notes: 'notes',
  ReportIncidents: 'report-incidents',
  Sessions: 'sessions',
  StudentAssessments: 'student-assessments',
  Tutoring: 'tutoring',
}

// Exact definition keys. Use for per-notification icons, navigation, and
// sidebar `notifications.definitions` counts.
export const NotificationDefinitions = {
  // Actors
  StudentIntakeCompletedCounselor: 'actors.student-intake-completed.counselor',
  StudentActivatedCounselor: 'actors.student-activated.counselor',
  StudentActivatedParent: 'actors.student-activated.parent',

  // Chat
  ChatMessageReceivedRecipient: 'chat.message-received.recipient',

  // College admissions
  CollegeApplicationSubmittedCounselor: 'college-admissions.application-submitted.counselor',
  CollegeReviewSessionScheduledStudent: 'college-admissions.review-session-scheduled.student',
  CollegeApplicationReviewedStudent: 'college-admissions.application-reviewed.student',
  CollegeApplicationFeedbackStudent: 'college-admissions.application-feedback.student',
  EssayReviewSubmittedCounselor: 'college-admissions.essay-review-submitted.counselor',
  EssayFeedbackStudent: 'college-admissions.essay-feedback.student',
  EssayReviewCompletedStudent: 'college-admissions.essay-review-completed.student',
  AppliedUniversityEssayFeedbackStudent:
    'college-admissions.applied-university-essay-feedback.student',
  // Not in the Sep-17 catalogue; kept until Mudassir confirms whether it is retired.
  UniversityRecommendationStudent: 'college-admissions.university-recommendation.student',
  UniversityApplicationRecordedCounselor:
    'college-admissions.university-application-recorded.counselor',
  AdmissionDecisionCounselor: 'college-admissions.admission-decision.counselor',

  // Commerce
  CommerceOrderRequestedParent: 'commerce.order-requested.parent',
  CommerceOrderRequestedStudent: 'commerce.order-requested.student',
  CommerceOrderDeniedStudent: 'commerce.order-denied.student',
  CommerceOrderDeniedParent: 'commerce.order-denied.parent',
  CommerceOrderPaidStudent: 'commerce.order-paid.student',
  CommerceOrderPaidParent: 'commerce.order-paid.parent',

  // Employee finances
  EmployeeFinancePaymentRecordedEmployee: 'employee-finances.payment-recorded.employee',

  // Idea Factory
  IdeaFactoryApplicationSubmittedStudent: 'idea-factory.application-submitted.student',
  IdeaFactoryApplicationSubmittedCounselor: 'idea-factory.application-submitted.counselor',
  IdeaFactoryMentorAssignedStudent: 'idea-factory.mentor-assigned.student',
  IdeaFactoryMentorAssignedMentor: 'idea-factory.mentor-assigned.mentor',
  IdeaFactoryProjectPlanSharedStudent: 'idea-factory.project-plan-shared.student',
  IdeaFactoryPhaseSubmittedMentor: 'idea-factory.phase-submitted.mentor',
  IdeaFactoryPhaseResubmissionEnabledStudent: 'idea-factory.phase-resubmission-enabled.student',

  // Identity (email-only — never shown in bell/sidebar)
  ParentAccountActivationEmail: 'identity.parent-account-activation.email',
  StudentAccountActivationEmail: 'identity.student-account-activation.email',
  RoleAccountActivationEmail: 'identity.role-account-activation.email',
  StudentEmailVerificationEmail: 'identity.student-email-verification.email',
  PasswordResetEmail: 'identity.password-reset.email',

  // Internships
  InternshipApplicationSubmittedCounselor: 'internships.application-submitted.counselor',
  InternshipGroupCreatedStudent: 'internships.group-created.student',
  InternshipGroupCreatedMentor: 'internships.group-created.mentor',
  InternshipProjectAssignedStudent: 'internships.project-assigned.student',
  InternshipTaskSubmittedMentor: 'internships.task-submitted.mentor',
  InternshipTaskResubmissionEnabledStudent: 'internships.task-resubmission-enabled.student',
  InternshipIssueReportedMentor: 'internships.issue-reported.mentor',
  InternshipIssueFeedbackAddedStudent: 'internships.issue-feedback-added.student',
  InternshipProgressReportSubmittedMentor: 'internships.progress-report-submitted.mentor',
  InternshipProjectCompletedStudent: 'internships.project-completed.student',
  InternshipProjectCompletedCounselor: 'internships.project-completed.counselor',
  InternshipCertificateIssuedStudent: 'internships.certificate-issued.student',
  InternshipMemberFailedStudent: 'internships.member-failed.student',

  // Notes
  NoteAddedStudent: 'notes.note-added.student',

  // Report incidents
  ReportIncidentSubmittedBamAdmin: 'report-incidents.submitted.bam-admin',

  // Sessions and counselor access
  TutorSessionScheduledStudent: 'sessions.tutor-session-scheduled.student',
  TutorSessionScheduledTutor: 'sessions.tutor-session-scheduled.tutor',
  CounselorSessionScheduledStudent: 'sessions.counselor-session-scheduled.student',
  CounselorAccessRequestedCounselor: 'sessions.counselor-access-requested.counselor',
  CounselorAccessRequestedStudent: 'sessions.counselor-access-requested.student',
  CounselorAccessRequestedParent: 'sessions.counselor-access-requested.parent',
  CounselorAccessPurchaseCompletedParent: 'sessions.counselor-access-purchase-completed.parent',
  CounselorAccessApprovedRequester: 'sessions.counselor-access-approved.requester',
  CounselorAccessDeniedRequester: 'sessions.counselor-access-denied.requester',
  SessionRecommendationCreatedParent: 'sessions.recommendation-created.parent',
  SessionRecommendationCreatedStudent: 'sessions.recommendation-created.student',
  SessionRecommendationApprovedCounselor: 'sessions.recommendation-approved.counselor',
  SessionRecommendationApprovedStudent: 'sessions.recommendation-approved.student',
  SessionRecommendationDeclinedCounselor: 'sessions.recommendation-declined.counselor',
  SessionRecommendationDeclinedStudent: 'sessions.recommendation-declined.student',
  SessionGiftGrantedStudent: 'sessions.gift-granted.student',
  SessionGivenBackParticipant: 'sessions.given-back.participant',

  // Student assessments
  BehaviorAgreementPublishedRecipient: 'student-assessments.behavior-agreement-published.recipient',
  PersonalityAssessmentCompletedRecipient:
    'student-assessments.personality-assessment-completed.recipient',
  CareerPathPublishedRecipient: 'student-assessments.career-path-published.recipient',
  // Sep-17: AI-review stage notifications for the counselor + return-for-revision to the student.
  PersonalityAssessmentReadyForReviewCounselor:
    'student-assessments.personality-assessment-ready-for-review.counselor',
  PersonalityAssessmentManualReviewRequiredCounselor:
    'student-assessments.personality-assessment-manual-review-required.counselor',
  PersonalityAssessmentReturnedStudent:
    'student-assessments.personality-assessment-returned.student',
  CareerPathReadyForReviewCounselor: 'student-assessments.career-path-ready-for-review.counselor',
  CareerPathManualReviewRequiredCounselor:
    'student-assessments.career-path-manual-review-required.counselor',
  CareerPathReturnedStudent: 'student-assessments.career-path-returned.student',

  // Tutoring
  TutoringCoursesRecommendedStudent: 'tutoring.courses-recommended.student',
  // Sep-17: tutoring engagement lifecycle (created, tutor assigned, status changed, progress).
  TutoringEngagementCreatedStudent: 'tutoring.engagement-created.student',
  TutoringEngagementCreatedParent: 'tutoring.engagement-created.parent',
  TutoringEngagementTutorAssignedStudent: 'tutoring.engagement-tutor-assigned.student',
  TutoringEngagementTutorAssignedParent: 'tutoring.engagement-tutor-assigned.parent',
  TutoringEngagementTutorAssignedTutor: 'tutoring.engagement-tutor-assigned.tutor',
  TutoringEngagementStatusChangedStudent: 'tutoring.engagement-status-changed.student',
  TutoringEngagementStatusChangedParent: 'tutoring.engagement-status-changed.parent',
  TutoringEngagementStatusChangedTutor: 'tutoring.engagement-status-changed.tutor',
  TutoringProgressReviewRequiredCounselor: 'tutoring.progress-review-required.counselor',
  TutoringProgressReviewedStudent: 'tutoring.progress-reviewed.student',
  TutoringProgressReviewedParent: 'tutoring.progress-reviewed.parent',
  TutoringProgressReviewedTutor: 'tutoring.progress-reviewed.tutor',
  TutoringAssignmentAssignedStudent: 'tutoring.assignment-assigned.student',
  TutoringAssignmentSubmittedTutor: 'tutoring.assignment-submitted.tutor',
  TutoringAssignmentMarkedStudent: 'tutoring.assignment-marked.student',
  TutoringQuizAssignedStudent: 'tutoring.quiz-assigned.student',
  TutoringQuizSubmittedTutor: 'tutoring.quiz-submitted.tutor',
  TutoringQuizMarkedStudent: 'tutoring.quiz-marked.student',
  TutoringAcademicResultRecordedCounselor: 'tutoring.academic-result-recorded.counselor',
  TutoringTranscriptUploadedCounselor: 'tutoring.transcript-uploaded.counselor',
  TutoringTutorRecommendationCreatedCounselor: 'tutoring.tutor-recommendation-created.counselor',
  TutoringTutorRecommendationStatusChangedTutor:
    'tutoring.tutor-recommendation-status-changed.tutor',
}

// moduleKey for a definition key = the segment before the first dot.
export const moduleKeyOf = (definitionKey) =>
  typeof definitionKey === 'string' ? definitionKey.split('.')[0] : ''
